import { NavLink, useLocation } from "react-router-dom";

import { cn } from "@/lib/utils";
import { CALENDLY_URL } from "@/constants";
import { langPath, useLang, useTranslation } from "@/i18n";

import { AnimatedClickable } from "../animatedClickable";
import { LanguageSwitcher } from "../languageSwitcher";
import { Button } from "../ui/button";

interface Link {
  name: string;
  path: string;
}

interface AppHeaderDesktopProps {
  links: Link[];
  className?: string;
}

export const AppHeaderDesktop: React.FC<AppHeaderDesktopProps> = ({
  links,
  className,
}) => {
  const { pathname } = useLocation();
  const lang = useLang();
  const t = useTranslation();
  const isHome = pathname === "/" || pathname === "/en";

  const scrollTo = (elementId: string) => {
    document.getElementById(elementId.replace('/#/', ''))?.scrollIntoView({ behavior: "smooth" });
  }

  return (
    <div
      className={cn(
        "sticky top-0 flex w-full flex-col bg-[#1d2128]",
        className,
      )}
      style={{ zIndex: 101 }}
    >
      <div className="flex w-full max-w-screen-xl m-auto flex-row items-center justify-between space-x-4 px-4 py-4">
        <NavLink to={langPath("/", lang)} className="no-focus cursor-pointer">
          <Logo />
        </NavLink>
        <div className="flex flex-row items-center space-x-8">
          <div className="flex flex-row space-x-4 align-center">
            {isHome && links.map((link, index) => link.path.startsWith('/#/') ? (
              <AnimatedClickable
                key={index}
                className={cn(
                  "h-fit cursor-pointer items-center justify-center rounded-full px-1 lg:px-4 py-2 text-sm text-[#ffffff]",
                  "border-transparent",
                )}
                onClick={() => scrollTo(link.path)}
              >
                {link.name}
              </AnimatedClickable>
            ) : (
              <AnimatedClickable
                key={index}
                className={cn(
                  "h-fit cursor-pointer items-center justify-center rounded-full px-1 lg:px-4 py-2 text-sm text-[#ffffff]",
                  "border-transparent",
                )}
              >
                <NavLink to={link.path} className="no-focus">
                  {link.name}
                </NavLink>
              </AnimatedClickable>
            ))}
          </div>
        </div>

        <div className="flex flex-row items-center justify-end space-x-4">
            <LanguageSwitcher />
            <div>
              <a href={CALENDLY_URL} target="_blank" rel="noreferrer" className="w-full inline-block">
                <Button shape="circle" className="bg-[#01C6FB] text-[#10141a] font-semibold border-0 hover:bg-[#4dd7fc]">
                  {t.header.cta}
                </Button>
              </a>
            </div>
        </div>
      </div>
    </div>
  );
};

const Logo: React.FC = () => (
  <div className="flex flex-row items-center space-x-3">
    <span className="text-md font-semibold text-[#01C6FB]">
      <img src="/images/rckntvlogo.png" alt="Rocknitive Logo" className="w-8 inline-block mr-2" />
      Rocknitive
    </span>
  </div>
);
